import type { UiLang } from "../lib/types";

/**
 * Hvor man kan få tak i det klesrådet sier man trenger.
 *
 * Samme spørsmål som `videoLink.ts` stiller for stedene: appen vet hva man
 * trenger — brodder, en powerbank, en dunjakke — og sa det uten å svare på det
 * neste, opplagte spørsmålet: hvor får jeg tak i det, om jeg ikke har det?
 *
 * Samme svar som der, og av samme grunn: et ekte søk, ikke en påstått
 * anbefaling. To alternativer ble vurdert og forkastet:
 *
 *   En bestemt butikks interne søk (for eksempel XXL, Norges største
 *   sportskjede) ville gitt en penere lenke, men URL-formatet for et slikt søk
 *   er ikke noe denne appen kan vite er riktig uten å teste det mot den ekte
 *   nettsiden — og en lenke som *ser* ut til å søke og lander på en
 *   feilmelding er verre enn ingen lenke. Se placeLink.ts for hvorfor det er
 *   regelen appen alt følger.
 *
 *   Googles «Shopping»-fane (`tbm=shop`) er en fristelse fordi den går rett til
 *   produkter, men parameteret er en intern detalj i Google sitt grensesnitt,
 *   ikke en dokumentert kontrakt — det har endret navn og oppførsel før uten
 *   varsel. Et vanlig søk med et kjøpsord foran er kjedeligere og mer stabilt:
 *   det er den samme URL-en Google har hatt i tjue år.
 *
 * Ordet foran er lagt til av samme grunn `videoSearchUrl` legger til landet:
 * «brodder» alene finner artikkelen om isbrodder på Wikipedia like gjerne som
 * en butikk, mens «kjøpe brodder» sorterer nettbutikker øverst.
 */

const BUY_WORD: Record<UiLang, string> = {
  no: "kjøpe",
  en: "buy",
  de: "kaufen",
  fr: "acheter",
  es: "comprar",
};

/**
 * Rydder bort forklaringen på slutten av enkelte råd.
 *
 * «reiseadapter — Norge bruker type C/F, 230 V» skal søke på «reiseadapter»,
 * ikke på hele setningen — spisskomma-halen er en opplysning til den som
 * leser lista, ikke en del av varen man leter etter.
 */
function withoutExplanation(label: string): string {
  return label.split(/\s+[—–]\s+/, 1)[0].trim();
}

export function packingSearchUrl(label: string, lang: UiLang): string {
  const query = `${BUY_WORD[lang]} ${withoutExplanation(label)}`;
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}
